import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { stopPropagation } from '../../utils/stop-propagation'
import { uniqueId } from '../../utils/unique-id'
import { CheckboxIcon } from './checkbox-icon'
import { CheckboxLabel } from './checkbox-label'

import styles from './checkbox.module.css'

const propTypes = {
    checked: PropTypes.bool,
    dataAttributes: PropTypes.shape({
        data: PropTypes.string
    }),
    disabled: PropTypes.bool,
    iconCheckedClassName: PropTypes.string,
    iconUncheckedClassName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    labelClassName: PropTypes.string,
    labelIcon: PropTypes.node,
    labelIconClassName: PropTypes.string,
    multiline: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
}

const defaultProps = {
    checked: false,
    disabled: false,
    multiline: false,
    onChange: Function.prototype,
    type: 'checkbox'
}

/**
 * Class representing a single checkbox input.
 *
 * @example <caption>Passing label as property</caption>
 * <code>
 *     <Checkbox label='Value A' value='a' name='example' />
 * </code>
 *
 * @example <caption>Passing label as child</caption>
 * <code>
 *     <Checkbox value='a' name='example'>
 *        Value A
 *     </Checkbox>
 * </code>
 *
 * @param {boolean} [props.checked=false] - The initial checked state
 * @param {string} [props.className] - Define additional className for the wrapper
 * @param {boolean} [props.disabled=false] - The disabled checkbox state
 * @param {Object} [props.dataAttributes] - Key Value Pairs for Data Attributes
 * @param {string} [props.iconCheckedClassName] - Additional className for the unchecked icon component
 * @param {string} [props.iconUncheckedClassName] - Additional className for the unchecked icon component
 * @param {string|number} [props.id] - The input id attribute, will be generated automatically if omitted
 * @param {ReactElement} [props.label] - The label element to be rendered
 * @param {string} [props.labelClassName] - Additional className for the label element
 * @param {ReactElement} [props.labelIcon] - The label icon element to be rendered
 * @param {string} [props.labelIconClassName] - Additional className for the label icon element
 * @param {boolean} [props.multiline=false] - Flag to enable different whitespace css rendering for long labels
 * @param {string} [props.name] - HTML checkbox name attribute
 * @param {Function} [props.onChange=noop] - Define callback handler when checkbox changed
 * @param {Object} [props.style] - Define additional styles for wrapper fieldset
 * @param {string} [props.type='checkbox'] - The input type attribute
 * @param {string|number|boolean} [props.value] - HTML checkbox value attribute
 * @returns {ReactElement} The rendered component
 */
class Checkbox extends Component {
    /**
     * The actual class constructor.
     *
     * @constructs
     * @param {Object} props - The component props
     * @returns {void}
     */
    constructor(props) {
        super(props)

        this.state = {
            checked: props.checked
        }

        this.onChange = this.onChange.bind(this)
        this.onWrapperClick = this.onWrapperClick.bind(this)
        this.bindInputRef = this.bindInputRef.bind(this)
    }

    /**
     * Setting the state after new props received.
     *
     * @param {Object} nextProps - The new component props
     * @returns {void}
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { checked } = nextProps

        if (checked !== this.state.checked) {
            this.setState({ checked })
        }
    }

    /**
     * The required render function to return react markup
     *
     * @returns {ReactElement} React component markup
     */
    render() {
        const {
            children,
            className,
            dataAttributes,
            disabled,
            iconCheckedClassName,
            iconUncheckedClassName,
            label,
            labelClassName,
            labelIcon,
            labelIconClassName,
            multiline,
            name,
            style,
            type,
            value
        } = this.props
        const { checked } = this.state
        const id = this.props.id || uniqueId()

        const classNameWrapper = cx(
            styles['checkbox'],
            {
                [styles['is-disabled']]: disabled,
                [styles['is-checked']]: checked,
                't3sel-checkbox-checked': checked,
                't3sel-checkbox-unchecked': !checked
            },
            className,
            't3sel-checkbox'
        )
        const classNameInput = cx(styles['checkbox-input'], 't3sel-checkbox-input')

        return (
            <div data-testid="checkbox" className={classNameWrapper} {...{ style }} onClick={this.onWrapperClick}>
                <input
                    data-testid="checkbox-input"
                    className={classNameInput}
                    onChange={this.onChange}
                    onClick={stopPropagation}
                    ref={this.bindInputRef}
                    {...dataAttributes}
                    {...{ checked, disabled, id, name, type, value }}
                />
                <CheckboxIcon
                    {...{
                        checked,
                        iconCheckedClassName,
                        iconUncheckedClassName
                    }}
                />
                <CheckboxLabel
                    {...{
                        id,
                        label,
                        labelClassName,
                        labelIcon,
                        labelIconClassName,
                        multiline,
                        onClick: stopPropagation
                    }}
                >
                    {children}
                </CheckboxLabel>
            </div>
        )
    }

    bindInputRef(element) {
        this.input = element
    }

    /**
     * Handle checkbox change event.
     *
     * @param {Object} event - The event object
     * @returns {void}
     */
    onChange(event) {
        const checked = !this.state.checked
        this.setState({ checked })
        this.props.onChange(event, checked)
    }

    onWrapperClick() {
        this.input.click()
    }
}

Checkbox.propTypes = propTypes
Checkbox.defaultProps = defaultProps

export { Checkbox, propTypes as checkboxProptypes }
